import { useEffect, useState } from 'react';

import cn from 'classnames';
import { AdminPermissions } from 'constants/admin-permissions';
import { AdminPermissionsDetails } from 'constants/admin-permissions';
import { MESSAGE_STATE_ERROR, MESSAGE_STATE_SUCCESS } from 'constants/message-app-state-contants';
import { useAccessControl } from 'hooks/access-control';
import { useDocumentTitle } from 'hooks/document-title';
import AdminPage from 'layouts/AdminPage/AdminPage';
import { useForm, useWatch } from 'react-hook-form';
import toast from 'react-hot-toast';
import Moment from 'react-moment';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'utils/requestHelpers';

import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import { AsyncSearch, Button, InputGroup, Toggle } from 'components/FormComponents';
import { IconButton } from 'components/IconButton/IconButton';
import { Table } from 'components/Table/Table';
import { CustomToast } from 'components/Toast/CustomToast';

import { messagePopUp } from 'api/app-slice';
import { selectAuthentication } from 'api/authentication-slice';
import { useLazyGetAllUsersQuery, useUpdateAdminPermissionsMutation } from 'api/user-api';

const AdminAdminUsers = () => {
  useDocumentTitle('Admin Users');

  const dispatch = useDispatch();
  const authentication = useSelector(selectAuthentication);
  const hasPermission = useAccessControl(authentication.admin_permissions);

  const [getUsers] = useLazyGetAllUsersQuery();
  const [updateAdminPermissions] = useUpdateAdminPermissionsMutation();

  const [showEditPermissionsModal, setShowEditPermissionsModal] = useState(false);
  const [showNewAdminUserModal, setShowNewAdminUserModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionUser, setActionUser] = useState();

  const [users, setUsers] = useState([]);

  const [pagination, setPagination] = useState({
    page: 1,
    records_per_page: 15,
    total_pages: 1,
    total_records: 0,
  });

  const fetchUsers = debounce(async (params = {}) => {
    try {
      setLoading(true);
      const { result } = await getUsers({
        ...params,
        with_admin_permissions: [AdminPermissions.ListCompanies],
      }).unwrap();
      setPagination(result.pagination);
      setUsers(result.users);
      setLoading(false);
    } catch (error) {
      console.error(error);
      toast.custom(
        t => (
          <CustomToast
            t={t}
            title="Error fetching users"
            message={String(
              error?.data?.message || error?.data?.error || error?.message || 'Unknown error',
            )}
            type="error"
          />
        ),
        {
          duration: 5000,
        },
      );
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleCreateAdminRole = async data => {
    setLoading(true);
    data.admin_permissions = [AdminPermissions.ListCompanies];
    const { data: result, error } = await updateAdminPermissions(data);

    if (data?.error) {
      dispatch(messagePopUp({ text: error.error.log, state: MESSAGE_STATE_ERROR, hide: true }));
    } else {
      const user = { ...result, admin_permissions: AdminPermissions.ListCompanies };
      setUsers(curr => [...curr, user]);
      dispatch(
        messagePopUp({
          text: `Admin access granted to ${user.email}.`,
          state: MESSAGE_STATE_SUCCESS,
          hide: true,
        }),
      );
      setShowNewAdminUserModal(false);
    }
    setLoading(false);
  };

  const handleEditPermissions = async permissionsData => {
    setLoading(true);
    const { error } = await updateAdminPermissions(permissionsData).unwrap();

    if (error) {
      dispatch(messagePopUp({ text: error.error.log, state: MESSAGE_STATE_ERROR, hide: true }));
    } else {
      dispatch(
        messagePopUp({
          text: 'Permissions updated successfully',
          state: MESSAGE_STATE_SUCCESS,
          hide: true,
        }),
      );
      setUsers(curr =>
        curr.map(user =>
          user.id === permissionsData.user_id
            ? { ...user, admin_permissions: permissionsData.admin_permissions.join(',') }
            : user,
        ),
      );
      setShowEditPermissionsModal(false);
      setActionUser(null);
    }
    setLoading(false);
  };

  const handleDeleteAdminRole = async userId => {
    setLoading(true);
    const { error } = await updateAdminPermissions({
      user_id: userId,
      admin_permissions: [],
    }).unwrap();

    if (error) {
      dispatch(messagePopUp({ text: error.error.log, state: MESSAGE_STATE_ERROR, hide: true }));
    } else {
      dispatch(
        messagePopUp({
          text: 'Admin permissions removed successfully',
          state: MESSAGE_STATE_SUCCESS,
          hide: true,
        }),
      );
      setUsers(curr => curr.filter(user => user.id !== userId));
    }
    setLoading(false);
  };

  return (
    <AdminPage
      loading={loading}
      title="Users"
      button={hasPermission(AdminPermissions.ManageAdminUsers)}
      buttonText="Grant Admin Access to User"
      onClick={() => setShowNewAdminUserModal(true)}
      allowedRole={AdminPermissions.ListAdminUsers}>
      <EditPermissionsModal
        visible={showEditPermissionsModal}
        onClose={() => setShowEditPermissionsModal(false)}
        user={actionUser}
        onSave={handleEditPermissions}
      />

      <NewAdminUserModal
        visible={showNewAdminUserModal}
        onClose={() => setShowNewAdminUserModal(false)}
        onSave={handleCreateAdminRole}
        getUsers={getUsers}
      />

      <Table.Context onQueryUpdate={fetchUsers} defaultQuery={{ page: 1 }}>
        <Table.TableContent className="u-margin--large">
          <Table.Filters>
            <Table.Search placeholder="Search for a user..." />
          </Table.Filters>
          <Table className="u-margin--large">
            <Table.Head>
              <Table.Row>
                <Table.Header>ID</Table.Header>
                <Table.Header sortKey="users.first_name">Name</Table.Header>
                <Table.Header sortKey="users.email">Email</Table.Header>
                <Table.Header sortKey="users.created_at">Created At</Table.Header>
                <Table.Header>Last Login</Table.Header>
                <Table.Header>Actions</Table.Header>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {users.map(user => (
                <Table.Row key={user.id}>
                  <Table.Col>{user.id}</Table.Col>
                  <Table.Col>
                    {user.first_name} {user.last_name}
                  </Table.Col>
                  <Table.Col>{user.email}</Table.Col>
                  <Table.Col>
                    <Moment format="DD/MM/YYYY">{user.created_at}</Moment>
                  </Table.Col>
                  <Table.Col>
                    {user.last_login && (
                      <Moment fromNow utc>
                        {user.last_login}
                      </Moment>
                    )}
                  </Table.Col>

                  <Table.Col>
                    {hasPermission(AdminPermissions.ManageAdminUsers) && (
                      <>
                        <IconButton
                          small
                          icon="edit"
                          tip="Edit permissions"
                          onClick={() => {
                            setActionUser(user);
                            setShowEditPermissionsModal(true);
                          }}
                        />
                        <IconButton
                          small
                          icon="trash"
                          tip="Remove all admin permissions"
                          className="u-margin-left--small"
                          onClick={() => handleDeleteAdminRole(user.id)}
                        />
                      </>
                    )}
                  </Table.Col>
                </Table.Row>
              ))}
            </Table.Body>
            <Table.Pagination {...pagination} />
          </Table>
        </Table.TableContent>
      </Table.Context>
    </AdminPage>
  );
};

export default AdminAdminUsers;

const NewAdminUserModal = ({
  visible,
  onClose = () => {},
  onSave = () => {},
  getUsers = () => {},
}) => {
  const { register, handleSubmit } = useForm();

  const asyncUsersSearch = async query => {
    const { data: response } = await getUsers({ search: query });

    if (response?.result?.users?.length < 1) return [];

    return response.result.users.map(user => ({
      name: `${user.first_name} ${user.last_name} • ${user.email}`,
      value: user.id,
    }));
  };

  return (
    <AnimatedModal visible={visible} onClose={onClose} title="Enable Admin Access" small>
      <form onSubmit={handleSubmit(onSave)}>
        <InputGroup title="User *">
          <AsyncSearch
            name="user_id"
            register={register}
            placeholder="Search for a user..."
            asyncSearch={asyncUsersSearch}
          />
        </InputGroup>
        <Button submit className="u-margin-top--large u-width-100">
          Grant Admin Access
        </Button>
      </form>
    </AnimatedModal>
  );
};

const EditPermissionsModal = ({ visible, user = null, onClose = () => {}, onSave = () => {} }) => {
  const [enabledPermissions, setEnabledPermissions] = useState([]);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      admin_permissions: user?.admin_permissions || [],
    },
  });

  const adminPermissionsValues = useWatch({ control, name: 'admin_permissions' });

  // useEffect(() => {
  //   setEnabledPermissions(Object.keys(adminPermissionsValues).filter(key => adminPermissionsValues[key]));
  // }, [adminPermissionsValues]);

  useEffect(() => {
    const userPermissions = user?.admin_permissions?.split(',') || [];

    const currentPermissions = Object.values(AdminPermissionsDetails).reduce(
      (permissions, permission) => {
        permissions[permission.slug] = userPermissions.includes(permission.slug);
        return permissions;
      },
      {},
    );

    reset({
      admin_permissions: currentPermissions,
    });
  }, [user]);

  const handleSave = data => {
    const adminPermissions = Object.keys(data.admin_permissions)
      .filter(key => !!data.admin_permissions[key])
      .map(key => key);
    onSave({ user_id: user.id, admin_permissions: adminPermissions });
  };

  return (
    <AnimatedModal visible={visible} onClose={onClose} title="Edit Permissions" small>
      <form onSubmit={handleSubmit(handleSave)}>
        {(Object.values(AdminPermissionsDetails) || []).map(permission => (
          <div
            key={permission.slug}
            className={cn(
              'u-margin-bottom',
              'd-flex',
              'align-items-center',
              'justify-content-between',
            )}>
            <div>
              <p className="u-margin-bottom--tiny">{permission.name}</p>
              <p className="t-small u-margin-bottom--0">{permission.description}</p>
            </div>
            <Toggle
              name={`admin_permissions.${permission.slug}`}
              tooltip={permission.description}
              register={register}
            />
          </div>
        ))}

        <Button submit className="u-margin-top--large u-width-100">
          Save
        </Button>
      </form>
    </AnimatedModal>
  );
};
